import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import AuthApi from '../../service/Api/authApi';
import { gaEvents } from '../../utils/analytics';
import { readBirthInfo, toBirthDateTime } from '../../utils/birthInfo';
import { fetchUser, setHasBirthChartReport } from './authSlice';
import { appendNewMessageToChat, triggerAnswerIntoViewScroll } from './chatsSlice';

/**
 * Birth chart flow + report state (HORO-10360).
 *
 * `flow` is one state machine, rendered globally by `BirthChartFlow`:
 *   steps: intro | offer | date | time | place | verify | loading
 * `details` is the same shape the backend puts on a `birth_chart_*` CTA:
 *   { birthDate: 'YYYY-MM-DD' | '', birthTime: 'HH:mm' | null,
 *     birthTimeKnown: boolean | null, place: {placeId, description} | null }
 * `report` is the server's BirthChartReportDTO, rendered as-is by BirthChartView.
 */
export const FLOW_STEP = {
    INTRO: 'intro',
    OFFER: 'offer',
    DATE: 'date',
    TIME: 'time',
    PLACE: 'place',
    VERIFY: 'verify',
    LOADING: 'loading'
};

export const FLOW_SOURCE = {
    PILL: 'pill',
    NUDGE: 'nudge',
    SIDEBAR: 'sidebar',
    PROFILE: 'profile',
    CHAT_VERIFY: 'chat_verify',
    CHAT_OFFER: 'chat_offer',
    AUTH_RESUME: 'auth_resume'
};

export const EMPTY_DETAILS = { birthDate: '', birthTime: null, birthTimeKnown: null, place: null };

const MISSING_TO_STEP = { date: FLOW_STEP.DATE, time: FLOW_STEP.TIME, place: FLOW_STEP.PLACE };

const initialState = {
    report: null,
    // idle | loading | succeeded | notFound | failed
    status: 'idle',
    error: null,
    flow: {
        open: false,
        step: null,
        // Where to go after the current single-field edit — 'verify' when the
        // user tapped a pencil, so they land back on the summary.
        returnTo: null,
        source: null,
        chatId: null,
        // Offer → "Continue to Chat": only the date is saved, no chart built.
        dateOnly: false,
        details: EMPTY_DETAILS
    },
    chartViewOpen: false,
    // Where the open chart view started (BIRTH_CHART_VIEW_SURFACE); reported with the view.
    chartViewSurface: null
};

// Matches the backend's BIRTH_CHART_VIEW_SURFACE (POST /user/birth-chart/view).
export const BIRTH_CHART_VIEW_SURFACE = {
    CHAT_CARD: 'chatCard',
    PROFILE: 'profile',
    SIDEBAR: 'sidebar',
    POST_GENERATE: 'postGenerate'
};

const isComplete = (details) => !!details.birthDate && (!!details.birthTime || details.birthTimeKnown === false) && !!details.place;

/** Where the flow starts, given how it was opened. */
export const resolveStartStep = ({ source, details, missing }) => {
    if (source === FLOW_SOURCE.CHAT_OFFER) return FLOW_STEP.OFFER;
    if (source === FLOW_SOURCE.CHAT_VERIFY || source === FLOW_SOURCE.AUTH_RESUME) {
        const first = (missing || []).map((m) => MISSING_TO_STEP[m]).find(Boolean);
        if (first) return first;
        return isComplete(details) ? FLOW_STEP.VERIFY : FLOW_STEP.DATE;
    }
    return FLOW_STEP.INTRO;
};

/** The POST /user/birth-chart body for a set of flow details. */
export const toGenerateBody = (details, chatId) => {
    const [year, month, day] = details.birthDate.split('-').map(Number);
    const timeKnown = !!details.birthTime;
    const [hour, minute] = timeKnown ? details.birthTime.split(':').map(Number) : [null, null];
    return {
        birthDateTime: toBirthDateTime({ year, month, day, hour, minute, timeKnown }),
        birthTimeKnown: timeKnown,
        birthAddress: { placeId: details.place.placeId, description: details.place.description },
        ...(chatId ? { chatId } : {})
    };
};

/** Flow details from the profile's stored birth data, for prefilling. */
export const detailsFromUser = (user) => {
    const info = readBirthInfo(user);
    if (!info) return EMPTY_DETAILS;
    const pad = (n) => String(n).padStart(2, '0');
    const address = user?.birthAddress;
    return {
        birthDate: `${info.year}-${pad(info.month)}-${pad(info.day)}`,
        birthTime: info.timeKnown ? `${pad(info.hour)}:${pad(info.minute ?? 0)}` : null,
        birthTimeKnown: typeof address?.birthTimeKnown === 'boolean' ? address.birthTimeKnown : info.timeKnown ? true : null,
        place: address?.placeId ? { placeId: address.placeId, description: address.description || '' } : null
    };
};

/**
 * Flow details to PRE-FILL from the profile. The server sends `user.birthDetails`:
 * the date and time the chart is built from, read at the birthplace. Use it when it
 * came from a stored local time or a birthplace; it is exact, where re-reading the
 * stored instant here would use the viewer's browser zone. With `source: 'date-only'`
 * (no birthplace) the server withholds the time, so fall back to `detailsFromUser`,
 * which reads whatever the profile holds (the profile page shows the same).
 */
export const prefillFromUser = (user) => {
    const fallback = detailsFromUser(user);
    const resolved = user?.birthDetails;
    if (!resolved?.birthDate || resolved.source === 'date-only') return fallback;
    return {
        birthDate: resolved.birthDate,
        birthTime: resolved.birthTime || null,
        birthTimeKnown: typeof resolved.birthTimeKnown === 'boolean' ? resolved.birthTimeKnown : null,
        place: fallback.place
    };
};

export const generateBirthChart = createAsyncThunk(
    'birthChart/generate',
    async ({ body, chatId }, { dispatch, getState, rejectWithValue }) => {
        const startedAt = Date.now();
        try {
            const response = await AuthApi.generateBirthChart(body);
            const { report, messages } = response.data;
            gaEvents.bcGenerateLatency(Date.now() - startedAt);
            dispatch(setHasBirthChartReport(true));
            // The server saved the birth details (and the signs computed from them)
            // to the profile; the copy in the store is still whatever it was before.
            // Without this the profile shows nothing until a reload — most visibly
            // for someone who just signed up through the anonymous flow.
            dispatch(fetchUser());

            // The details bubble and the "chart is ready" reply — only when the
            // chat is still the one open, otherwise they'd land in the wrong thread.
            if (chatId && Array.isArray(messages) && getState().chats.activeChat.chatId === chatId) {
                messages.forEach((message) => dispatch(appendNewMessageToChat(message)));
                // The chart view is still covering the thread; have it scrolled to the new
                // messages by the time it closes.
                dispatch(triggerAnswerIntoViewScroll());
            }
            return report;
        } catch (error) {
            return rejectWithValue(error?.response?.data?.error || error?.message || 'Could not build your chart');
        }
    }
);

export const fetchBirthChart = createAsyncThunk('birthChart/fetch', async (_, { rejectWithValue }) => {
    try {
        const response = await AuthApi.getBirthChart();
        // "No chart yet" arrives as a 200 with `report: null` — a 404 here would be
        // caught by the app-wide 404 handler and replace the page with the homepage.
        // Rejecting with status 404 keeps the 'notFound' state and the fallback in
        // `loadBirthChart` (build from the profile's details) working as before.
        if (!response.data.report) return rejectWithValue({ status: 404, message: 'No birth chart yet' });
        return response.data.report;
    } catch (error) {
        return rejectWithValue({ status: error?.response?.status, message: error?.message });
    }
});

/**
 * Page-load path: GET, and on "no report yet" build one from the details
 * already on the profile. Resolves to the report, or null when the profile has
 * nothing to build from.
 */
export const loadBirthChart = createAsyncThunk('birthChart/load', async (_, { dispatch, getState }) => {
    const existing = await dispatch(fetchBirthChart());
    if (fetchBirthChart.fulfilled.match(existing)) return existing.payload;

    const { user } = getState().auth;
    const stored = detailsFromUser(user);
    if (!stored.birthDate || !stored.place) return null;

    const built = await dispatch(generateBirthChart({ body: toGenerateBody(stored) }));
    return generateBirthChart.fulfilled.match(built) ? built.payload : null;
});

const birthChartSlice = createSlice({
    name: 'birthChart',
    initialState,
    reducers: {
        openBirthChartFlow(state, { payload = {} }) {
            const { source = FLOW_SOURCE.PILL, details, missing, chatId = null, dateOnly = false } = payload;
            const merged = { ...EMPTY_DETAILS, ...(details || {}) };
            state.flow = {
                open: true,
                step: resolveStartStep({ source, details: merged, missing }),
                returnTo: null,
                source,
                chatId,
                dateOnly,
                details: merged
            };
        },
        // Post-auth resume of an anonymous "Generate": details are complete, so go
        // straight to the loader — BirthChartFlow starts the request from there.
        openBirthChartLoading(state, { payload = {} }) {
            state.flow = {
                ...initialState.flow,
                open: true,
                step: FLOW_STEP.LOADING,
                source: FLOW_SOURCE.AUTH_RESUME,
                chatId: payload.chatId || null,
                details: { ...EMPTY_DETAILS, ...(payload.details || {}) }
            };
        },
        setFlowStep(state, { payload }) {
            state.flow.step = payload;
        },
        // A pencil on Verify: jump to a step and come back afterwards.
        editFlowStep(state, { payload }) {
            state.flow.step = payload;
            state.flow.returnTo = FLOW_STEP.VERIFY;
        },
        // Offer → "Continue to Chat": Verify then saves only the date.
        setFlowDateOnly(state, { payload }) {
            state.flow.dateOnly = !!payload;
        },
        patchFlowDetails(state, { payload }) {
            state.flow.details = { ...state.flow.details, ...payload };
        },
        // Move on from a completed field step: back to Verify after a pencil
        // edit, otherwise to whatever `next` says.
        advanceFlow(state, { payload: next }) {
            if (state.flow.returnTo) {
                state.flow.step = state.flow.returnTo;
                state.flow.returnTo = null;
            } else {
                state.flow.step = next;
            }
        },
        closeBirthChartFlow(state) {
            state.flow = { ...initialState.flow };
        },
        openChartView(state, { payload }) {
            state.chartViewOpen = true;
            state.chartViewSurface = payload?.surface || null;
        },
        closeChartView(state) {
            state.chartViewOpen = false;
            state.chartViewSurface = null;
        },
        resetBirthChart() {
            return initialState;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(generateBirthChart.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(generateBirthChart.fulfilled, (state, { payload }) => {
                state.status = 'succeeded';
                state.report = payload;
            })
            .addCase(generateBirthChart.rejected, (state, { payload }) => {
                state.status = 'failed';
                state.error = payload;
            })
            .addCase(fetchBirthChart.pending, (state) => {
                if (!state.report) state.status = 'loading';
            })
            .addCase(fetchBirthChart.fulfilled, (state, { payload }) => {
                state.status = 'succeeded';
                state.report = payload;
            })
            .addCase(fetchBirthChart.rejected, (state, { payload }) => {
                state.status = payload?.status === 404 ? 'notFound' : 'failed';
            })
            // One user's chart must not greet the next person on a shared browser.
            // Matched by type rather than importing `logOut`, which keeps this
            // slice from depending on authSlice's thunks.
            .addMatcher(
                (action) => action.type === 'auth/logout/fulfilled',
                () => initialState
            );
    }
});

export const birthChartReducer = birthChartSlice.reducer;
export const {
    openBirthChartFlow,
    openBirthChartLoading,
    setFlowStep,
    editFlowStep,
    patchFlowDetails,
    setFlowDateOnly,
    advanceFlow,
    closeBirthChartFlow,
    openChartView,
    closeChartView,
    resetBirthChart
} = birthChartSlice.actions;

export const getBirthChartReport = (state) => state.birthChart.report;
export const getBirthChartStatus = (state) => state.birthChart.status;
export const getBirthChartError = (state) => state.birthChart.error;
export const getBirthChartFlow = (state) => state.birthChart.flow;
export const getChartViewOpen = (state) => state.birthChart.chartViewOpen;
export const getChartViewSurface = (state) => state.birthChart.chartViewSurface;
